import { createFileRoute, Link } from "@tanstack/react-router";
import { useMemo, useState } from "react";
import { SiteFooter } from "@/components/site-footer";
import { useWebMcpState } from "@/lib/webmcp/use-webmcp";
import { invokeTool } from "@/lib/webmcp/runtime";
import { WebMcpLibraryTools } from "@/components/webmcp-provider";
import { SITE } from "@/lib/site-content";
import { ogImageMeta } from "@/lib/og-images";

const TITLE = "WebMCP on vibing.inc — browser-native tools for AI agents";
const DESC =
  "vibing.inc registers WebMCP tools on document.modelContext, so an in-browser AI agent can search 2,000+ curated vibe-coding resources, read the page, compare gems, navigate and file submissions — no API key, no server round trip.";

export const Route = createFileRoute("/webmcp")({
  head: () => ({
    meta: [
      { title: TITLE },
      { name: "description", content: DESC },
      { property: "og:title", content: TITLE },
      { property: "og:description", content: DESC },
      { property: "og:type", content: "website" },
      { property: "og:url", content: "https://vibing.inc/webmcp" },
      ...ogImageMeta("for-agents", "Share card: WebMCP on vibing.inc"),
      { name: "twitter:card", content: "summary_large_image" },
    ],
    links: [{ rel: "canonical", href: "https://vibing.inc/webmcp" }],
    scripts: [
      {
        type: "application/ld+json",
        children: JSON.stringify({
          "@context": "https://schema.org",
          "@type": "TechArticle",
          headline: TITLE,
          description: DESC,
          url: "https://vibing.inc/webmcp",
          author: { "@type": "Organization", name: SITE.name, url: SITE.url },
        }),
      },
    ],
  }),
  component: WebMcpPage,
});

const STATUS: Record<string, { label: string; detail: string }> = {
  native: {
    label: "Native WebMCP detected",
    detail: "Your browser implements document.modelContext — tools are live for any agent in this tab.",
  },
  polyfill: {
    label: "Running on the polyfill",
    detail:
      "Your browser has no native WebMCP yet, so we loaded @mcp-b/global. Tools are still callable by MCP-B compatible clients and by the console below.",
  },
  unavailable: {
    label: "WebMCP unavailable",
    detail: "Neither a native implementation nor the polyfill could be initialised in this browser.",
  },
  unknown: { label: "Detecting…", detail: "Checking for WebMCP support." },
};

function WebMcpPage() {
  const { availability, tools } = useWebMcpState();
  const status = STATUS[availability] ?? STATUS.unknown;

  return (
    <>
      {/* The demo console is more useful with the full library tool set loaded. */}
      <WebMcpLibraryTools />
      <main id="main" className="mx-auto w-full max-w-4xl px-5 pb-24 pt-24 sm:px-8">
        <p className="text-xs uppercase tracking-[0.2em] text-muted-foreground">
          Agent surface
        </p>
        <h1 className="mt-4 text-[clamp(2rem,5vw,3.25rem)] font-semibold leading-[1.05] tracking-tight">
          This website is a tool server.
        </h1>
        <p className="mt-5 max-w-2xl text-base leading-relaxed text-muted-foreground">
          WebMCP lets a page hand tools directly to the AI agent running in the browser.
          Every page here registers tools on <code>document.modelContext</code> — the agent
          searches the library, reads the page, compares resources and navigates for the
          user, with no API key and no server round trip.
        </p>

        <section
          aria-labelledby="status"
          className="mt-10 rounded-2xl border border-border/60 bg-muted/20 p-5"
        >
          <h2 id="status" className="text-sm font-semibold">
            {status.label}
          </h2>
          <p className="mt-1 text-sm text-muted-foreground">{status.detail}</p>
          <p className="mt-3 text-sm">
            <strong>{tools.length}</strong> tools registered on this page right now.
          </p>
        </section>

        <ToolConsole />

        <section aria-labelledby="how" className="mt-14">
          <h2 id="how" className="text-xl font-semibold tracking-tight">
            How to use it
          </h2>
          <ol className="mt-4 space-y-3 text-sm leading-relaxed text-muted-foreground">
            <li>
              <strong className="text-foreground">1.</strong> Open any page on vibing.inc in a
              browser with an agent that speaks WebMCP (Chrome's origin trial, an MCP-B
              extension, or an automation client bridged into the tab).
            </li>
            <li>
              <strong className="text-foreground">2.</strong> The agent calls
              <code> document.modelContext.getTools()</code> and sees the tools listed above,
              scoped to the page it is on.
            </li>
            <li>
              <strong className="text-foreground">3.</strong> Ask in natural language — "find me a
              WebGPU shader playground in the Vibing library and open it" — and the agent chains{" "}
              <code>library_search</code> → <code>library_open_gem</code>.
            </li>
          </ol>
        </section>

        <section aria-labelledby="safety" className="mt-14">
          <h2 id="safety" className="text-xl font-semibold tracking-tight">
            Safety model
          </h2>
          <ul className="mt-4 space-y-2 text-sm leading-relaxed text-muted-foreground">
            <li>
              Read tools are annotated <code>readOnlyHint</code>; anything that navigates,
              likes or submits is not, so agents surface it for confirmation.
            </li>
            <li>
              Tools returning third-party text (gem descriptions, page content) carry{" "}
              <code>untrustedContentHint</code> — that content is data, never instructions.
            </li>
            <li>
              Submissions go to the same rate-limited, validated review queue humans use.
              Nothing an agent sends is ever published automatically.
            </li>
            <li>
              No account, admin, billing or client-data tool is ever exposed to the page
              agent — the WebMCP surface is strictly the public site.
            </li>
          </ul>
        </section>

        <section aria-labelledby="other" className="mt-14">
          <h2 id="other" className="text-xl font-semibold tracking-tight">
            Prefer a server?
          </h2>
          <p className="mt-3 text-sm leading-relaxed text-muted-foreground">
            The same capabilities are available headlessly: a public MCP server at{" "}
            <code>/api/public/mcp</code>, a CORS-open JSON API, markdown twins of every page
            and <code>llms.txt</code>. See{" "}
            <Link to="/for-agents" className="underline underline-offset-4">
              /for-agents
            </Link>{" "}
            and{" "}
            <Link to="/docs" className="underline underline-offset-4">
              the developer docs
            </Link>
            .
          </p>
        </section>
      </main>
      <SiteFooter />
    </>
  );
}

function ToolConsole() {
  const { tools } = useWebMcpState();
  const names = useMemo(() => tools.map((t) => t.tool.name), [tools]);
  const [selected, setSelected] = useState<string>("");
  const [args, setArgs] = useState('{\n  "query": "webgpu"\n}');
  const [output, setOutput] = useState<string>("");
  const [busy, setBusy] = useState(false);

  const active = selected || names[0] || "";
  const tool = tools.find((t) => t.tool.name === active);

  async function run() {
    setBusy(true);
    setOutput("");
    try {
      const parsed = args.trim() ? JSON.parse(args) : {};
      const result = await invokeTool(active, parsed);
      setOutput(JSON.stringify(result, null, 2));
    } catch (err) {
      setOutput(`Error: ${err instanceof Error ? err.message : String(err)}`);
    } finally {
      setBusy(false);
    }
  }

  return (
    <section aria-labelledby="console" className="mt-12">
      <h2 id="console" className="text-xl font-semibold tracking-tight">
        Try a tool
      </h2>
      <p className="mt-2 text-sm text-muted-foreground">
        This console calls the exact same handlers an agent would.
      </p>

      <div className="mt-5 grid gap-3 sm:grid-cols-[minmax(0,18rem)_1fr]">
        <label className="block text-sm">
          <span className="sr-only">Tool</span>
          <select
            value={active}
            onChange={(e) => setSelected(e.target.value)}
            className="w-full min-h-[44px] rounded-lg border border-border/60 bg-background px-3 text-sm"
          >
            {names.map((n) => (
              <option key={n} value={n}>
                {n}
              </option>
            ))}
          </select>
        </label>
        <div className="text-sm text-muted-foreground">{tool?.tool.description}</div>
      </div>

      <label className="mt-4 block text-sm">
        <span className="text-xs uppercase tracking-wide text-muted-foreground">
          Arguments (JSON)
        </span>
        <textarea
          value={args}
          onChange={(e) => setArgs(e.target.value)}
          rows={5}
          spellCheck={false}
          className="mt-1 w-full rounded-lg border border-border/60 bg-background p-3 font-mono text-xs"
        />
      </label>

      <button
        type="button"
        onClick={run}
        disabled={busy || !active}
        className="mt-3 inline-flex min-h-[44px] items-center rounded-lg bg-primary px-4 text-sm font-medium text-primary-foreground disabled:opacity-50"
      >
        {busy ? "Running…" : "Run tool"}
      </button>

      {output && (
        <pre className="mt-4 max-h-96 overflow-auto rounded-lg border border-border/60 bg-muted/30 p-4 font-mono text-xs">
          {output}
        </pre>
      )}
    </section>
  );
}
